import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Collapse,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import gsap from 'gsap';
import { useTranslation } from 'react-i18next';
import { todoAPI, areaAPI } from '../services/api';
import TodoItem from './TodoItem';
import TodoForm from './TodoForm';
import useRiseIn from '../hooks/useRiseIn';
import { prefersReducedMotion } from '../utils/motion';
import styles from './AreaSection.module.css';

function ProgressBar({ pct, onAddTask }) {
  const { t } = useTranslation();
  const complete = pct === 100;
  const pctRef = useRef(null);
  const trackRef = useRef(null);
  const prevPctRef = useRef(pct);

  useEffect(() => {
    const prev = prevPctRef.current;
    prevPctRef.current = pct;
    if (pct !== 100 || prev === 100 || prefersReducedMotion()) return undefined;

    const delay = 0.55;
    const tweens = [
      gsap.fromTo(
        pctRef.current,
        { scale: 1.3 },
        { scale: 1, duration: 0.45, delay, ease: 'back.out(3)', clearProps: 'transform' }
      ),
      gsap.fromTo(
        trackRef.current,
        { boxShadow: '0 0 0 0 rgba(39, 174, 96, 0.35)' },
        {
          boxShadow: '0 0 0 8px rgba(39, 174, 96, 0)',
          duration: 0.7,
          delay,
          ease: 'power2.out',
          clearProps: 'boxShadow',
        }
      ),
    ];

    return () => tweens.forEach((tween) => tween.kill());
  }, [pct]);

  return (
    <Box className={styles.progressSection}>
      <Box className={styles.progressHeader}>
        <Typography
          ref={pctRef}
          className={`${styles.progressPct} ${complete ? styles.progressPctDone : ''}`}
        >
          {pct}%
        </Typography>
        <Typography onClick={onAddTask} className={styles.addTaskBtn}>
          {t('areas.addTask')}
        </Typography>
      </Box>
      <Box ref={trackRef} className={styles.progressTrack}>
        <Box
          className={`${styles.progressFill} ${complete ? styles.progressFillDone : ''}`}
          style={{ '--pct': pct + '%' }}
        />
      </Box>
    </Box>
  );
}

const AreaSection = ({ area, todos, expanded, onToggleExpanded, onUpdate, onDelete, onToggle, index }) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState(area.name);
  const [loading, setLoading] = useState(false);
  const [localTodos, setLocalTodos] = useState(todos);
  const [showAddForm, setShowAddForm] = useState(false);

  const cardRef = React.useRef(null);
  useRiseIn(cardRef, { delay: (index || 0) * 0.06 });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
  );

  React.useEffect(() => {
    setLocalTodos((prev) => {
      // Preserve local order; only update item data from the incoming prop
      const updated = prev
        .map((local) => todos.find((t) => t.id === local.id) || null)
        .filter(Boolean);
      const newTodos = todos.filter((t) => !prev.some((p) => p.id === t.id));
      return [...updated, ...newTodos];
    });
  }, [todos]);

  const total = todos.length;
  const done = todos.filter((t) => t.completed).length;
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  const left = total - done;

  const handleSaveName = async () => {
    if (!name.trim()) return;
    setLoading(true);
    try {
      await areaAPI.update(area.id, name);
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating area:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setName(area.name);
    setIsEditing(false);
  };

  const handleDeleteArea = async () => {
    const todoCount = todos.length;
    const message =
      todoCount > 0
        ? t('areas.deleteConfirmWithTasks', { name: area.name, count: todoCount })
        : t('areas.deleteConfirm', { name: area.name });

    if (window.confirm(message)) {
      setLoading(true);
      try {
        await areaAPI.delete(area.id);
        if (onDelete) onDelete();
      } catch (error) {
        console.error('Error deleting area:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = localTodos.findIndex((t) => t.id === active.id);
    const newIndex = localTodos.findIndex((t) => t.id === over.id);
    const reordered = arrayMove(localTodos, oldIndex, newIndex);

    setLocalTodos(reordered);

    try {
      await todoAPI.reorder(reordered.map((t) => t.id));
    } catch (err) {
      console.error('Error saving order:', err);
      setLocalTodos(todos);
    }
  };

  return (
    <Box ref={cardRef} className={styles.card}>
      {/* Header */}
      <Box className={styles.header}>
        <Box className={styles.headerLeft}>
          <IconButton
            size="small"
            onClick={onToggleExpanded}
            className={styles.expandBtn}
          >
            {expanded ? (
              <ExpandLessIcon className={styles.expandIcon} />
            ) : (
              <ExpandMoreIcon className={styles.expandIcon} />
            )}
          </IconButton>

          {isEditing ? (
            <Box className={styles.editNameRow}>
              <TextField
                size="small"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                className={styles.nameField}
              />
              <IconButton
                size="small"
                onClick={handleSaveName}
                disabled={loading || !name.trim()}
                className={styles.saveNameBtn}
              >
                <SaveIcon className={styles.saveNameIcon} />
              </IconButton>
              <IconButton size="small" onClick={handleCancelEdit} disabled={loading}>
                <CancelIcon className={styles.cancelNameIcon} />
              </IconButton>
            </Box>
          ) : (
            <>
              <Box>
                <Typography className={styles.areaName}>
                  {area.name}
                </Typography>
                <Typography className={styles.taskCount}>
                  {t('areas.taskCount', { count: total })}
                </Typography>
              </Box>
            </>
          )}
        </Box>

        <Box className={styles.headerRight}>
          {!isEditing && (
            <>
              <IconButton
                size="small"
                onClick={() => setIsEditing(true)}
                disabled={loading}
                className={styles.editBtn}
              >
                <EditIcon className={styles.editAreaIcon} />
              </IconButton>
              <IconButton
                size="small"
                onClick={handleDeleteArea}
                disabled={loading}
                className={styles.deleteAreaBtn}
              >
                <DeleteIcon className={styles.deleteAreaIcon} />
              </IconButton>
            </>
          )}
          <Typography className={styles.leftBadge}>
            {t('areas.leftBadge', { count: left })}
          </Typography>
        </Box>
      </Box>

      {/* Tasks */}
      <Collapse in={expanded}>
        {localTodos.length === 0 ? (
          <Typography className={styles.emptyState}>
            {t('areas.noTasks')}
          </Typography>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={localTodos.map((t) => t.id)}
              strategy={verticalListSortingStrategy}
            >
              <Box className={styles.taskList}>
                {localTodos.map((todo) => (
                  <TodoItem
                    key={todo.id}
                    todo={todo}
                    onUpdate={onUpdate}
                    onDelete={onDelete}
                    onToggle={onToggle}
                  />
                ))}
              </Box>
            </SortableContext>
          </DndContext>
        )}

        <TodoForm
          areaId={area.id}
          onTodoAdded={onUpdate}
          open={showAddForm}
          onToggle={() => setShowAddForm(!showAddForm)}
        />

        <ProgressBar pct={pct} onAddTask={() => setShowAddForm(!showAddForm)} />
      </Collapse>
    </Box>
  );
};

export default AreaSection;
